import { useMemo, useState } from "react";
import SiteHeader from "../../components/feature/SiteHeader";
import SiteFooter from "../../components/feature/SiteFooter";
import PageHero from "../../components/feature/PageHero";
import usePageTitle from "../../hooks/usePageTitle";
import { seasons } from "../../mocks/episodes";
import { articles } from "../../mocks/articles";
import { Link } from "react-router-dom";

const tint: Record<string, { badge: string; icon: string }> = {
  rose: { badge: "bg-primary-100 text-primary-700", icon: "ri-heart-line" },
  mint: { badge: "bg-secondary-100 text-secondary-800", icon: "ri-leaf-line" },
  amber: { badge: "bg-accent-100 text-accent-800", icon: "ri-star-smile-line" },
};

export default function Episodes() {
  usePageTitle("分集剧情");
  const [activeId, setActiveId] = useState("s1");
  const [query, setQuery] = useState("");
  const [openNo, setOpenNo] = useState<string | null>("第1集");

  const season = seasons.find((s) => s.id === activeId) ?? seasons[0];
  const t = tint[season.color] ?? tint.rose;

  const list = useMemo(() => {
    const kw = query.trim();
    if (!kw) return season.episodes;
    return season.episodes.filter(
      (e) => e.title.includes(kw) || e.no.includes(kw) || e.synopsis.includes(kw)
    );
  }, [season, query]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `守护甜心 - ${season.name}分集剧情`,
    description: `守护甜心${season.name}全部分集剧情速览与剧情介绍（${season.range}）。`,
    url: "https://sequelio.github.io/episodes",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sequelio.github.io/" },
        { "@type": "ListItem", position: 2, name: "分集剧情", item: "https://sequelio.github.io/episodes" },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SiteHeader />
      <main>
        <PageHero
          crumbs={[{ label: "首页", path: "/" }, { label: "分集剧情" }]}
          title="分集剧情"
          subtitle="守护甜心三部曲的分集剧情速览，从第一集到最终话按顺序梳理。切换到对应季，点开任意一集即可查看剧情走向。"
          badge="按序梳理 · 一键对照"
        />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-full bg-background-100 p-1" role="tablist">
              {seasons.map((s) => {
                const active = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => {
                      setActiveId(s.id);
                      setQuery("");
                      setOpenNo(null);
                    }}
                    className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                      active ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 rounded-full bg-background-100 px-4 py-2">
              <i className="ri-search-line text-foreground-500 text-sm" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`搜索${season.name}剧集`}
                className="w-40 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none"
                aria-label="搜索剧集"
              />
            </label>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <aside className="lg:col-span-1">
              <div className="overflow-hidden rounded-2xl border border-background-200/70 bg-background-50">
                <div className="h-56 overflow-hidden">
                  <img
                    src={season.cover}
                    alt={`${season.name}剧情介绍封面`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${t.badge}`}>
                    <i className={t.icon} />
                    {season.name}
                  </span>
                  <h2 className="mt-3 font-display text-2xl text-foreground-950">{season.name}</h2>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-600">
                    <span>共 {season.count}</span>
                    <span>{season.range}</span>
                    <span>{season.period}</span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-foreground-700">{season.intro}</p>
                </div>
              </div>
              <Link
                to="/analysis"
                className="mt-4 flex items-center justify-between rounded-2xl bg-accent-100 p-5 transition-colors hover:bg-accent-200"
              >
                <div>
                  <p className="font-medium text-accent-900">想看深层解读？</p>
                  <p className="text-xs text-accent-800 mt-1">角色、设定与结局解析都在这里</p>
                </div>
                <i className="ri-arrow-right-line text-accent-800" />
              </Link>
            </aside>

            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-xl text-foreground-950">
                  {season.name} 剧集列表
                  <span className="ml-2 text-sm text-foreground-500 font-sans">{list.length} 集速览</span>
                </h2>
              </div>
              {list.length === 0 ? (
                <p className="rounded-2xl border border-background-200/70 bg-background-50 p-8 text-center text-sm text-foreground-600">
                  没有找到与「{query}」相关的剧集，换个关键词试试。
                </p>
              ) : (
                <ul className="space-y-3">
                  {list.map((ep) => {
                    const open = openNo === ep.no;
                    return (
                      <li
                        key={ep.no}
                        className={`overflow-hidden rounded-2xl border transition-colors ${
                          open ? "border-primary-200 bg-primary-50/40" : "border-background-200/70 bg-background-50"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenNo(open ? null : ep.no)}
                          className="flex w-full items-center gap-4 px-5 py-4 text-left"
                          aria-expanded={open}
                        >
                          <span className="w-16 shrink-0 font-display text-base text-primary-600">{ep.no}</span>
                          <span className="flex-1 font-medium text-foreground-950">{ep.title}</span>
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg transition-transform ${
                              open ? "rotate-45 bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700"
                            }`}
                          >
                            <i className="ri-add-line" />
                          </span>
                        </button>
                        {open && (
                          <p className="px-5 pb-5 pl-20 text-sm leading-7 text-foreground-700">{ep.synopsis}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-14 rounded-3xl border border-background-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="font-display text-xl text-foreground-950">边看边查，相关剧情解析</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {articles.slice(0, 3).map((a) => (
                <Link
                  key={a.id}
                  to={`/analysis/${a.id}`}
                  className="group rounded-2xl border border-background-200/70 bg-background-50 p-5 transition-colors hover:border-primary-200"
                >
                  <span className="text-xs text-primary-600">{a.category}</span>
                  <h3 className="mt-2 font-medium text-foreground-950 group-hover:text-primary-700">{a.title}</h3>
                  <p className="mt-2 text-xs text-foreground-600 line-clamp-2">{a.summary}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}